import React, { useState, useEffect, useRef } from "react";
import { MapPin, Crosshair, CircleNotch } from "@phosphor-icons/react";
import { api } from "../../lib/api";

export const AddressAutocomplete = ({ value, onSelect, placeholder, showCurrent, testid, bare = false }) => {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => { setQuery(value || ""); }, [value]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (!query || query.length < 3) { setResults([]); return; }
    if (query === value) return;
    setLoading(true);
    const id = setTimeout(async () => {
      try {
        const { data } = await api.get("/geocode/suggest", { params: { q: query } });
        setResults(data.results || []);
        setOpen(true);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const choose = (item) => { onSelect(item); setQuery(item.label); setOpen(false); };

  const useCurrent = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { latitude, longitude } = pos.coords;
        const { data } = await api.get("/geocode/reverse", { params: { lat: latitude, lon: longitude } });
        if (data.label) choose({ label: data.label, lat: latitude, lon: longitude });
      } finally { setLocating(false); }
    }, () => setLocating(false), { enableHighAccuracy: true, timeout: 10000 });
  };

  return (
    <div className="relative" ref={boxRef}>
      <div className={bare ? "flex items-center gap-3 w-full" : "flex items-center gap-3 bg-white border border-[#071B2F]/12 rounded-2xl px-4 py-3.5 focus-within:border-[#D4AF37] focus-within:shadow-[0_0_0_4px_rgba(212,175,55,0.12)] transition-all"}>
        <MapPin weight="fill" size={18} className="text-[#D4AF37] shrink-0" />
        <input
          data-testid={testid}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          placeholder={placeholder}
          className="cfg-body w-full bg-transparent outline-none text-[#071B2F] placeholder:text-[#071B2F]/35 text-[15px]"
        />
        {loading && <CircleNotch size={18} className="text-[#071B2F]/40 animate-spin shrink-0" />}
        {showCurrent && (
          <button type="button" onClick={useCurrent} data-testid={`${testid}-current`} title="Ma position actuelle" className="shrink-0 text-[#071B2F]/40 hover:text-[#D4AF37] transition-colors">
            <Crosshair weight="bold" size={17} className={locating ? "animate-spin" : ""} />
          </button>
        )}
      </div>

      {open && results.length > 0 && (
        <div className="absolute z-30 left-0 right-0 mt-2 bg-white border border-[#071B2F]/10 rounded-2xl shadow-[0_24px_60px_-20px_rgba(7,27,47,0.35)] overflow-hidden">
          {results.map((r, i) => (
            <button
              key={i}
              type="button"
              data-testid={`${testid}-option-${i}`}
              onClick={() => choose(r)}
              className="cfg-body w-full text-left px-4 py-3 text-[13px] text-[#071B2F]/80 hover:bg-[#F4F5F7] transition-colors flex items-start gap-2 border-b border-[#071B2F]/5 last:border-0"
            >
              <MapPin weight="regular" size={16} className="text-[#D4AF37] mt-0.5 shrink-0" />
              <span className="line-clamp-2">{r.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
